import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, Theme, Paper } from "@material-ui/core";
import { useHistory, useRouteMatch } from "react-router";
import Grid from "@material-ui/core/Grid";
import moment from "moment";
import {
  KButton,
  KTextInput,
  KDatePicker,
  KCropperWrapper,
  KSelectAutocomplete,
} from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSetState";
import KCheckboxSelect from "../../components/ui/KCheckboxSelect";
import { countries } from "../../components/constants/countries";
import { drivingCategories } from "../../components/constants/drivingCategories";
import { getUsers } from "../../apiCalls/admin/users";
import { addDriver, getDriver, editDriver } from "../../apiCalls/admin/drivers";
import { setAlert } from "../../redux/actions/actions";
import BaseLayout from "../../components/layouts/BaseLayout";
import { getAuthUploadUrl } from "../../apiCalls/imageUploads/authUpload";

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    borderRadius: 4,
  },
  gridRoot: {
    overflow: "scroll",
    height: "calc(100vh - 144px)",
  },
  title: {
    color: theme.palette.common.black,
    fontSize: 16,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      width: 300,
    },
  },
  buttons: {
    width: "100%",
    height: 72,
    display: "flex",
    justifyContent: "flex-end",
    position: "fixed",
    padding: "16px 48px 0 0",
    bottom: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    background: "white",
  },
  save: {
    marginLeft: 16,
  },
  paper: {
    padding: 32,
    boxShadow: "none",
  },
}));

const NewDriver: React.FC = () => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const history: any = useHistory();
  const routeBack = (): void => history.goBack();
  const match = useRouteMatch({
    path: "/admin/drivers/:driverId",
    strict: true,
    sensitive: true,
  });
  const driverId =
    (match?.params as any)?.driverId !== "create"
      ? (match?.params as any)?.driverId
      : undefined;
  const company = useSelector(
    (state: any) => state.customer.currentCustomer?.shortname,
  );

  const renderTitle = (): string => {
    return driverId ? "Edit Driver" : "Add Driver";
  };

  const [
    {
      avatar,
      licenseFront,
      licenseBack,
      user,
      userId,
      firstname,
      lastname,
      phone,
      email,
      birthdate,
      country,
      category,
      documentNr,
      issueDate,
      expiryDate,
      driverCardNumber,
      driverCardIssueDate,
      driverCardExpiryDate,
      driverIdentification,
      users,
      disabled,
    },
    setState,
  ] = useSetState({
    avatar: "",
    licenseFront: "",
    licenseBack: "",
    user: {},
    userId: null,
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    birthdate: null,
    country: "",
    category: "",
    documentNr: "",
    issueDate: null,
    expiryDate: null,
    driverCardNumber: "",
    driverCardIssueDate: null,
    driverCardExpiryDate: null,
    driverIdentification: "",
    users: [],
    disabled: false,
  });

  const personalInfo = [
    {
      name: "firstname",
      value: firstname,
      label: "Firstname",
      style: { display: "inline-block", marginRight: "16px" },
      width: "192px",
    },
    {
      name: "lastname",
      value: lastname,
      label: "Lastname",
      style: { display: "inline-block" },
      width: "192px",
    },
    {
      name: "phone",
      value: phone,
      label: "Phone",
      width: "400px",
    },
    {
      name: "email",
      value: email,
      label: "Email",
      width: "400px",
    },
  ];

  const licenseInfo = [
    {
      name: "birthdate",
      value: birthdate,
      placeholder: "dd.mm.yyyy",
      label: "Birthdate",
      style: { display: "inline-block", marginRight: 16 },
      endIcon: "calendar",
    },

    {
      name: "category",
      value: category,
      style: {
        width: "192px",
        display: "inline-block",
        marginTop: "0px !important",
      },
      label: "Category",
      placeholder: "Categories",
      elements: drivingCategories,
    },
    {
      name: "country",
      value: country,
      label: "Country",
      style: { width: "400px" },
      placeholder: "Select Country",
      elements: countries,
    },
    {
      name: "documentNr",
      value: documentNr,
      label: "Document Nr",
    },
    {
      name: "issueDate",
      value: issueDate,
      placeholder: "dd.mm.yyyy",
      style: { display: "inline-block", marginRight: 16 },
      label: "Issue Date",
      endIcon: "calendar",
    },
    {
      name: "expiryDate",
      value: expiryDate,
      placeholder: "dd.mm.yyyy",
      style: { display: "inline-block" },
      label: "Expiry Date",
      endIcon: "calendar",
    },
  ];

  const driverIdField = [
    {
      name: "driverIdentification",
      value: driverIdentification,
      label: "Driver ID",
      placeholder: "iButton / RFID Number",
    },
  ];

  const driverCard = [
    {
      name: "driverCardNumber",
      value: driverCardNumber,
      label: "Driver Card Number",
      placeholder: "Driver Card Number",
    },
    {
      name: "driverCardIssueDate",
      value: driverCardIssueDate,
      placeholder: "dd.mm.yyyy",
      style: { display: "inline-block", marginRight: 16 },
      label: "Issue Date",
      endIcon: "calendar",
    },
    {
      name: "driverCardExpiryDate",
      value: driverCardExpiryDate,
      placeholder: "dd.mm.yyyy",
      style: { display: "inline-block" },
      label: "Expiry Date",
      endIcon: "calendar",
    },
  ];

  const handleChange = (e): void => {
    setState({
      user: e,
      userId: e._id || null,
      firstname: e.first_name || "",
      lastname: e.last_name || "",
      phone: e.phone_number || "",
      email: e.email || "",
      disabled: true,
      avatar: e.avatar || null,
    });
  };

  const handleReset = (): void => {
    setState({
      avatar: "",
      licenseFront: "",
      licenseBack: "",
      user: {},
      userId: null,
      firstname: "",
      lastname: "",
      phone: "",
      email: "",
      birthdate: null,
      country: "",
      category: [],
      documentNr: "",
      issueDate: null,
      expiryDate: null,
      driverCardNumber: "",
      driverCardIssueDate: null,
      driverCardExpiryDate: null,
      rfid: "",
      iButton: "",
      disabled: false,
    });
  };

  const handleSelectChange = (name, value): void => {
    setState({ [name]: value.label });
  };

  const handleCheckboxSelectChange = (value): void => {
    setState({ category: value });
  };

  const handleSave = () => {
    if (driverId) {
      editDriver(company, driverId, {
        user_id: userId,
        first_name: firstname,
        last_name: lastname,
        avatar,
        email: email || null,
        phone_number: phone,
        birth_date: birthdate ? moment(birthdate).format("YYYY-MM-DD") : null,
        driver_license: {
          issuing_country: country,
          document_number: documentNr,
          issue_date: issueDate ? moment(issueDate).format("YYYY-MM-DD") : null,
          expiry_date: expiryDate
            ? moment(expiryDate).format("YYYY-MM-DD")
            : null,
          categories: category,
          front_page: licenseFront || null,
          back_page: licenseBack || null,
        },
        driver_identification: driverIdentification,
        tacho_card: {
          document_number: driverCardNumber,
          issue_date: driverCardIssueDate
            ? moment(driverCardIssueDate).format("YYYY-MM-DD")
            : null,
          expiry_date: driverCardExpiryDate
            ? moment(driverCardIssueDate).format("YYYY-MM-DD")
            : null,
        },
      })
        .then((res) => {
          if (!res.detail && !res.errors) {
            routeBack();
          } else {
            dispatch(setAlert(Object.values(res.detail[0]), "error"));
          }
        })
        .catch((e) => console.error(e));
    }
    if (!driverId) {
      addDriver(company, {
        user_id: userId,
        first_name: firstname,
        last_name: lastname,
        avatar,
        email: email || null,
        phone_number: phone,
        birth_date: birthdate ? moment(birthdate).format("YYYY-MM-DD") : null,
        driver_license: {
          issuing_country: country,
          document_number: documentNr,
          issue_date: issueDate ? moment(issueDate).format("YYYY-MM-DD") : null,
          expiry_date: expiryDate
            ? moment(expiryDate).format("YYYY-MM-DD")
            : null,
          categories: category,
          front_page: licenseFront || null,
          back_page: licenseBack || null,
        },
        driver_identification: driverIdentification,
        tacho_card: {
          document_number: driverCardNumber,
          issue_date: driverCardIssueDate
            ? moment(driverCardIssueDate).format("YYYY-MM-DD")
            : null,
          expiry_date: driverCardExpiryDate
            ? moment(driverCardExpiryDate).format("YYYY-MM-DD")
            : null,
        },
      })
        .then((res) => {
          if (!res.detail && !res.errors) {
            routeBack();
          } else {
            dispatch(setAlert(Object.values(res.detail[0]), "error"));
          }
        })
        .catch((e) => console.error(e));
    }
  };

  const handleTextInputChange = (e): void => {
    setState({ [e.target.name]: e.target.value });
  };

  const getAllUsers = (): void => {
    getUsers(company).then((response) => {
      setState({
        users: response.map((x) => ({
          ...x,
          label: `${x.first_name} ${x.last_name}`,
        })),
      });
    });
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  useEffect(() => {
    if (driverId) {
      getDriver(company, driverId).then((response) => {
        setState({
          userId: response.user_id || null,
          firstname: response.first_name || "",
          lastname: response.last_name || "",
          avatar: response.avatar,
          email: response.email || "",
          phone: response.phone_number || "",
          birthdate: response.birth_date || "",
          licenseFront: response.driver_license.front_page || "",
          licenseBack: response.driver_license.back_page || "",
          country: response.driver_license.issuing_country || "",
          documentNr: response.driver_license.document_number || "",
          issueDate: response.driver_license.issue_date || "",
          expiryDate: response.driver_license.expiry_date || "",
          category: response.driver_license.categories || "",
          driverIdentification: response.driver_identification || "",
          driverCardNumber: response.tacho_card.document_number || "",
          driverCardIssueDate: response.tacho_card.issue_date || "",
          driverCardExpiryDate: response.tacho_card.expiry_date || "",
        });
      });
    }
  }, []);

  const setAvatar = (name, url): void => {
    setState({ [name]: url });
  };

  const onDateChange = (dates, name): void => {
    setState({ [name]: dates });
  };

  useEffect(() => {
    setState({ user: { first_name: firstname, last_name: lastname, avatar } });
  }, [firstname, lastname, avatar, setState]);

  return (
    <BaseLayout title={renderTitle()} handleArrowClick={routeBack}>
      <div className={classes.root}>
        <Grid container spacing={6} className={classes.gridRoot}>
          <Grid item lg={6} md={6} sm={12}>
            <Paper className={classes.paper}>
              <h1 className={classes.title}>Personal Info</h1>
              <KSelectAutocomplete
                placeholder={
                  userId ? `${firstname} ${lastname}` : "Connect User"
                }
                disabled={false}
                elements={users}
                userId={userId}
                onChange={handleChange}
                hasAvatar
                handleReset={handleReset}
              />
              <KCropperWrapper
                title="Upload Photo"
                name="avatar"
                helperText="Allowed files are .PNG, .JPG and .JPEG"
                uploadPosition="right"
                width={250}
                height={250}
                borderRadius={200}
                setAvatar={setAvatar}
                user={user}
                fetchUrl={getAuthUploadUrl}
                uploadUrl={`${company}/drivers/upload_avatar`}
              />
              {personalInfo.map((field) => {
                return (
                  <KTextInput
                    key={field.name}
                    onChange={handleTextInputChange}
                    disabled={!!((disabled || driverId) && userId)}
                    name={field.name}
                    value={field.value}
                    placeholder={`${field.label}...`}
                    label={field.label}
                    style={field.style}
                    width={field.width}
                  />
                );
              })}
            </Paper>
          </Grid>
          <Grid item lg={6} md={6} sm={12}>
            <Paper className={classes.paper}>
              <h1 className={classes.title}>Driving License</h1>
              {licenseInfo.map((field, index) => {
                if (
                  index !== 0 &&
                  index !== 1 &&
                  index !== 2 &&
                  index !== 4 &&
                  index !== 5 &&
                  index !== 6
                ) {
                  return (
                    <KTextInput
                      key={field.name}
                      onChange={handleTextInputChange}
                      name={field.name}
                      value={field.value}
                      placeholder={
                        field.placeholder
                          ? field.placeholder
                          : `${field.label}...`
                      }
                      label={field.label}
                      style={field.style}
                      endIcon={field.endIcon}
                    />
                  );
                }
                if (index === 1) {
                  return (
                    <KCheckboxSelect
                      key={field.label}
                      elements={field.elements}
                      onChange={handleCheckboxSelectChange}
                      placeholder={field.placeholder}
                      label={field.label}
                      style={field.style}
                      value={field.value}
                    />
                  );
                }
                if (index === 0 || index === 4 || index === 5) {
                  return (
                    <KDatePicker
                      dateProps={{ showYearDropdown: true }}
                      label={field.label}
                      key={field.name}
                      onChange={(dates) => onDateChange(dates, field.name)}
                      selected={field.value ? new Date(field.value) : null}
                      style={field.style}
                      placeholder={field.placeholder}
                    />
                  );
                }
                return (
                  <KSelectAutocomplete
                    key={field.label}
                    elements={field.elements}
                    placeholder={field.placeholder}
                    name={field.name}
                    value={field.value}
                    onChange={handleSelectChange}
                    style={field.style}
                    label={field.label}
                  />
                );
              })}
              <h1 className={classes.title} style={{ marginTop: 24 }}>
                Driver Identification
              </h1>
              {driverIdField.map((field) => (
                <KTextInput
                  key={field.name}
                  onChange={handleTextInputChange}
                  name={field.name}
                  value={field.value}
                  placeholder={
                    field.placeholder ? field.placeholder : `${field.label}...`
                  }
                  label={field.label}
                />
              ))}
              <h1 className={classes.title} style={{ marginTop: 24 }}>
                Driver Card
              </h1>
              {driverCard.map((field, index) => {
                if (index === 0) {
                  return (
                    <KTextInput
                      key={field.name}
                      onChange={handleTextInputChange}
                      name={field.name}
                      style={field.style}
                      value={field.value}
                      placeholder={
                        field.placeholder
                          ? field.placeholder
                          : `${field.label}...`
                      }
                      label={field.label}
                    />
                  );
                }
                return (
                  <KDatePicker
                    dateProps={{ showYearDropdown: true }}
                    label={field.label}
                    key={field.name}
                    onChange={(dates) => onDateChange(dates, field.name)}
                    selected={field.value ? new Date(field.value) : null}
                    style={field.style}
                    placeholder={field.placeholder}
                  />
                );
              })}
            </Paper>
          </Grid>
          <Grid item lg={6} md={6} sm={12}>
            <Paper className={classes.paper}>
              <h1 className={classes.title}>Driver License</h1>
              <KCropperWrapper
                title="Front Side"
                name="licenseFront"
                uploadText="Choose File"
                helperText="Allowed files are .PNG, .JPG und .JPEG"
                uploadPosition="bottom"
                width={370}
                height={240}
                borderRadius={0}
                setAvatar={setAvatar}
                image={licenseFront}
                fetchUrl={getAuthUploadUrl}
                uploadUrl={`${company}/drivers/upload_license`}
                style={{ width: 370, height: 240 }}
              />
              <KCropperWrapper
                title="Back Side"
                name="licenseBack"
                uploadText="Choose File"
                helperText="Allowed files are .PNG, .JPG und .JPEG"
                uploadPosition="bottom"
                width={370}
                height={240}
                border={20}
                borderRadius={0}
                setAvatar={setAvatar}
                image={licenseBack}
                fetchUrl={getAuthUploadUrl}
                uploadUrl={`${company}/drivers/upload_license`}
                style={{ width: 370, height: 240 }}
              />
            </Paper>
          </Grid>
          <div className={classes.buttons}>
            <KButton children="Cancel" theme="secondary" onClick={routeBack} />
            <div className={classes.save}>
              <KButton
                children="Save and close"
                theme="primary"
                onClick={() => handleSave()}
              />
            </div>
          </div>
        </Grid>
      </div>
    </BaseLayout>
  );
};

export default NewDriver;
